import PageNavigation from "@components/navigation/pageNavigation";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import PageFooter from "@components/navigation/pageFooter";
import Image from "next/image";
import PageHeader from "@components/pageHeader";
import { ArrowNarrowRightIcon } from "@heroicons/react/solid";

export default function Documents({ baseUrl, certificates, documents }) {
  const { t } = useTranslation(["common", "downloads"]);
  return (
    <>
      <PageNavigation />
      <PageHeader
        title={t("other.documents.name")}
        description={t("other.documents.description")}
      />
      <aside className="mt-10 c-container !py-4">
        <a href="/logo.svg" className="block rounded bg-default !p-8" download>
          <article className="text-center md:text-left">
            <h1 className="text-2xl font-semibold text-white">
              {t("downloads:branding.header")}
            </h1>
            <p className="flex items-center justify-center text-white md:justify-start">
              {t("downloads:branding.description")}
              <ArrowNarrowRightIcon className="w-5 h-5 ml-1" />
            </p>
          </article>
        </a>
      </aside>
      <div className="rounded c-container">
        <h2 className="py-4 text-2xl font-semibold">
          {t("downloads:certificates")}
        </h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {certificates?.data?.map((certificate) => {
            const data = certificate.attributes;
            const certificateFile = data.certificate.data.attributes;
            const certificateIcon = data?.icon?.data?.attributes;

            return (
              <a
                href={
                  certificateFile ? `${baseUrl}${certificateFile.url}` : "#"
                }
                target="_blank"
                rel="noreferrer"
                key={certificate.id}
                className="p-6 font-semibold text-black transition rounded bg-neutral-100 dark:bg-neutral-800 dark:text-white hover:bg-default hover:text-white"
              >
                <span className="text-sm font-normal">{data.type}</span>
                <p className="text-xl font-semibold">{data.name}</p>
                {certificateIcon && (
                  <div className="mt-4">
                    <Image
                      src={baseUrl + certificateIcon.url}
                      width={certificateIcon.width}
                      height={certificateIcon.height}
                      alt={certificateIcon.alternativeText}
                    />
                  </div>
                )}
              </a>
            );
          })}
        </div>
      </div>
      <div className="c-container">
        <h2 className="py-4 text-2xl font-semibold">
          {t("downloads:documents")}
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {documents?.data?.map((document) => {
            const data = document.attributes;
            const file = data.file.data.attributes;

            return (
              <a
                href={file ? `${baseUrl}${file.url}` : "#"}
                target="_blank"
                rel="noreferrer"
                key={document.id}
                className="p-6 font-semibold text-black transition rounded bg-neutral-100 dark:bg-neutral-800 dark:text-white hover:bg-default hover:text-white"
              >
                <p>{data.name}</p>
              </a>
            );
          })}
        </div>
      </div>
      <PageFooter />
    </>
  );
}

export async function getStaticProps({ locale }) {
  const baseUrl = process.env.STRAPI_URL;

  try {
    const responseDocuments = await fetch(
      `${baseUrl}/api/documents?locale=${locale}&populate=*`,
      {
        headers: {
          Authorization: `Bearer ${process.env.STRAPI_KEY}`,
        },
      }
    );

    const responseCertificates = await fetch(
      `${baseUrl}/api/certificates?locale=${locale}&populate=*`,
      {
        headers: {
          Authorization: `Bearer ${process.env.STRAPI_KEY}`,
        },
      }
    );

    const documents = await responseDocuments.json();
    const certificates = await responseCertificates.json();

    return {
      props: {
        baseUrl,
        documents,
        certificates,
        ...(await serverSideTranslations(locale, ["common", "downloads"])),
      },
      revalidate: 60,
    };
  } catch (error) {
    console.log(error);

    return null;
  }
}
